import React, { useState, useEffect } from 'react';
import { analyticsAPI, builderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Building2, Users, Briefcase, Plus, Settings, Check, X,
  TrendingUp, Globe, MoreVertical, Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [newBuilder, setNewBuilder] = useState({
    name: '',
    slug: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    loadBuilders();
  }, []);

  const loadBuilders = async () => {
    try {
      const response = await builderAPI.list();
      setBuilders(response.data);
    } catch (error) {
      toast.error('Failed to load builders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBuilder = async (e) => {
    e.preventDefault();
    try {
      await builderAPI.create(newBuilder);
      toast.success('Builder created successfully');
      setDialogOpen(false);
      setNewBuilder({ name: '', slug: '', contact_email: '', contact_phone: '' });
      loadBuilders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create builder');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const stats = {
    totalBuilders: builders.length,
    activeBuilders: builders.filter(b => b.is_active !== false).length,
    totalModulesEnabled: builders.reduce((sum, b) => sum + (b.enabled_modules?.length || 0), 0),
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage all builders and platform settings</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-builder">
              <Plus className="h-4 w-4 mr-2" />
              Add Builder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Builder</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBuilder} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Builder Name *</Label>
                <Input 
                  value={newBuilder.name} 
                  onChange={(e) => setNewBuilder({
                    ...newBuilder, 
                    name: e.target.value,
                    slug: generateSlug(e.target.value)
                  })} 
                  required 
                  placeholder="e.g., Prestige Group"
                />
              </div>
              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <Input 
                  value={newBuilder.slug} 
                  onChange={(e) => setNewBuilder({...newBuilder, slug: e.target.value})} 
                  required 
                  placeholder="prestige-group"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input 
                  type="email"
                  value={newBuilder.contact_email} 
                  onChange={(e) => setNewBuilder({...newBuilder, contact_email: e.target.value})} 
                  placeholder="contact@builder.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input 
                  value={newBuilder.contact_phone} 
                  onChange={(e) => setNewBuilder({...newBuilder, contact_phone: e.target.value})} 
                  placeholder="+91 9876543210"
                />
              </div>
              <Button type="submit" className="w-full">Create Builder</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Building2 className="h-4 w-4" />
              <span className="text-sm">Total Builders</span>
            </div>
            <p className="text-3xl font-bold">{stats.totalBuilders}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Check className="h-4 w-4" />
              <span className="text-sm">Active Builders</span>
            </div>
            <p className="text-3xl font-bold text-emerald-700">{stats.activeBuilders}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Globe className="h-4 w-4" />
              <span className="text-sm">Total Modules Enabled</span>
            </div>
            <p className="text-3xl font-bold">{stats.totalModulesEnabled}</p>
          </CardContent>
        </Card>
      </div>

      {/* Builders List */}
      <Card>
        <CardHeader>
          <CardTitle>All Builders</CardTitle>
        </CardHeader>
        <CardContent>
          {builders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No builders yet</p>
              <p>Create your first builder to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {builders.map((builder) => (
                <div key={builder.id} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4">
                    <div 
                      className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: builder.branding?.primary_color || '#0F172A' }}
                    >
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{builder.name}</h3>
                        {builder.is_active !== false ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">/{builder.slug}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {builder.enabled_modules?.length || 0} modules enabled
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm">
                      <Settings className="h-4 w-4 mr-1" />
                      Configure
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>Edit Builder</DropdownMenuItem>
                        <DropdownMenuItem>Manage Users</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">
                          {builder.is_active !== false ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
